import { CustomAttributeDefinition } from '@provision/entity-types';
import { gql, QueryHookOptions, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { usePlatformCatalogAccess } from 'src/graphql/entities/group-rules/platform-catalog-access';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';
import { CustomAttributeOption } from './custom-attributes.types';

const CUSTOM_ATTRIBUTE_DEFINITION_FIELDS = gql`
  fragment CustomAttributeDefinitionFields on CustomAttributeDefinition {
    id
    customAttributeDefinitionId
    partnerId
    entity
    definition
    createdAt
  }
`;

const GET_CUSTOM_ATTRIBUTE_DEFINITION_QUERY = gql`
  query GetCustomAttributeDefinition($params: QueryParams)
  @entity(name: "CustomAttributeDefinition") {
    getCustomAttributeDefinition(queryParams: $params) {
      records {
        ...CustomAttributeDefinitionFields
      }
    }
  }
  ${CUSTOM_ATTRIBUTE_DEFINITION_FIELDS}
`;

export interface CustomAttribute {
  customAttributeDefinitionId?: string;
  partnerId?: number;
  entity?: string;
  // stable, charset-safe identifier assigned by the backend at creation. The JSON
  // key a record's value is stored under; decoupled from the mutable displayName.
  key?: string;
  displayName: string;
  type: { label: string; id: string };
  deleted?: boolean;
  isDefault?: boolean;
  relatedEntity?: string;
  group?: string;
  options?: CustomAttributeOption[];
  filterable?: boolean;
  visibility: {
    visibleToAllUsers: boolean;
    restrictedToUserGroups?: string[];
  };
  defaultValue?: string | string[] | boolean;
}

export interface CustomAttributeGroup {
  customAttributeDefinitionId?: string;
  partnerId?: number;
  entity?: string;
  displayName: string;
  key: string;
  deleted?: boolean;
  isEditing?: boolean;
  relatedEntity?: string;
  isNew?: boolean;
}

export interface AttributeDefinition {
  attributes: Record<string, Omit<CustomAttribute, 'type'> & { type: string }>;
  groups?: CustomAttributeGroup[];
}

interface GetCustomAttributeDefinitions {
  getCustomAttributeDefinition: Records<CustomAttributeDefinition>;
}

export const CREATE_CUSTOM_ATTRIBUTE_DEFINITION_MUTATION = gql`
  mutation CreateCustomAttributeDefinition($data: CustomAttributeDefinitionDataInput) {
    createCustomAttributeDefinition(data: $data) {
      ...CustomAttributeDefinitionFields
    }
  }
  ${CUSTOM_ATTRIBUTE_DEFINITION_FIELDS}
`;

export const UPDATE_CUSTOM_ATTRIBUTE_DEFINITION_MUTATION = gql`
  mutation UpdateCustomAttributeDefinition(
    $conditions: [ConditionInput]
    $data: CustomAttributeDefinitionDataInput
  ) {
    updateCustomAttributeDefinition(conditions: $conditions, data: $data) {
      ...CustomAttributeDefinitionFields
    }
  }
  ${CUSTOM_ATTRIBUTE_DEFINITION_FIELDS}
`;

export const useCustomAttributeDefinition = (
  options?: QueryHookOptions<GetCustomAttributeDefinitions> & {
    desc?: boolean;
    filters?: ConditionInput[];
  },
) => {
  const { desc = true, filters = [] } = options ?? {};

  const { showErrorAlert } = useNotifications();

  const [updateCustomAttributeDefinitionMutation] = useMutation<{
    updateCustomAttributeDefinition: Partial<
      CustomAttributeDefinition & {
        __typename: string;
      }
    >[];
  }>(UPDATE_CUSTOM_ATTRIBUTE_DEFINITION_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong updating the custom attribute, please try again');
    },
  });

  const updateCustomAttributeDefinition = useCallback(
    async ({
      entity,
      definition,
      partnerId,
      customAttributeDefinitionId,
    }: {
      entity?: string | null;
      definition: string;
      customAttributeDefinitionId: string;
      partnerId?: number;
    }) => {
      if (!customAttributeDefinitionId || !entity) {
        return;
      }
      const { data } = await updateCustomAttributeDefinitionMutation({
        variables: {
          conditions: [
            {
              attribute: 'customAttributeDefinitionId',
              operator: '=',
              value: customAttributeDefinitionId,
            },
          ],
          data: {
            definition,
          },
        },
        optimisticResponse: {
          updateCustomAttributeDefinition: [
            {
              __typename: 'CustomAttributeDefinition',
              id: customAttributeDefinitionId,
              customAttributeDefinitionId,
              entity,
              definition,
              partnerId,
              createdAt: new Date().toISOString(),
            },
          ],
        },
        update: (cache, { data: response }) => {
          const updated = response?.updateCustomAttributeDefinition[0];
          if (!updated) return;

          const id = cache.identify({
            __typename: 'CustomAttributeDefinition',
            id: updated.customAttributeDefinitionId,
          });

          const fields = generateFieldModifiersMap({
            definition: updated.definition,
          });

          cache.modify({
            id,
            fields,
          });
        },
      });

      return data?.updateCustomAttributeDefinition;
    },
    [updateCustomAttributeDefinitionMutation],
  );

  const deleteCustomAttribute = useCallback(
    async ({
      customAttribute,
      customAttributeDefinition,
    }: {
      customAttribute: CustomAttribute;
      customAttributeDefinition?: CustomAttributeDefinition;
    }) => {
      if (!customAttributeDefinition?.customAttributeDefinitionId) {
        return [];
      }

      const definition = parseJsonSafely<AttributeDefinition>(
        customAttributeDefinition.definition,
      );
      if (definition?.attributes[customAttribute.displayName]) {
        definition.attributes[customAttribute.displayName].deleted = true;
      }
      const updatedCustomAttributeDefinition = await updateCustomAttributeDefinition({
        ...customAttributeDefinition,
        definition: JSON.stringify(definition),
      });

      return updatedCustomAttributeDefinition;
    },
    [updateCustomAttributeDefinition],
  );

  const [createCustomAttributeDefinitionMutation] = useMutation<{
    createCustomAttributeDefinition: Partial<
      CustomAttributeDefinition & {
        __typename: string;
      }
    >[];
  }>(CREATE_CUSTOM_ATTRIBUTE_DEFINITION_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong adding a new custom attribute, please try again');
    },
  });

  const createCustomAttributeDefinition = useCallback(
    async ({
      entity,
      definition,
      partnerId,
    }: {
      entity?: string | null;
      definition: string;
      partnerId?: number;
    }) => {
      if (!entity) {
        return;
      }
      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createCustomAttributeDefinitionMutation({
        variables: {
          data: {
            entity,
            partnerId,
            definition,
          },
        },
        optimisticResponse: {
          createCustomAttributeDefinition: [
            {
              __typename: 'CustomAttributeDefinition',
              id: tempId,
              customAttributeDefinitionId: tempId,
              entity,
              definition,
              partnerId,
              createdAt,
            },
          ],
        },
        update: (cache, { data: response }) => {
          const created = response?.createCustomAttributeDefinition[0];
          if (!created) return;

          cache.updateQuery<GetCustomAttributeDefinitions, QueryVariables>(
            {
              query: GET_CUSTOM_ATTRIBUTE_DEFINITION_QUERY,
              variables: {
                params: {
                  filters,
                  sorting: { attribute: 'updatedAt', desc },
                },
              },
            },
            (existingData) => {
              if (!existingData?.getCustomAttributeDefinition) {
                return;
              }

              const records = [
                ...existingData.getCustomAttributeDefinition.records.filter(
                  (attributeDefinition) =>
                    !(
                      attributeDefinition.entity === created.entity &&
                      attributeDefinition.partnerId === created.partnerId
                    ),
                ),
                created as CustomAttributeDefinition,
              ];

              return {
                getCustomAttributeDefinition: {
                  records,
                },
              };
            },
          );
        },
      });

      return data?.createCustomAttributeDefinition;
    },
    [createCustomAttributeDefinitionMutation, desc, filters],
  );

  const saveCustomAttributeDefinition = useCallback(
    async ({
      entity,
      definition,
      partnerId,
      customAttributeDefinition,
    }: {
      entity?: string | null;
      definition: string;
      customAttributeDefinition?: CustomAttributeDefinition | null;
      partnerId?: number;
    }) => {
      let customAttribute: Partial<CustomAttributeDefinition>[] | undefined;
      if (customAttributeDefinition?.customAttributeDefinitionId) {
        customAttribute = await updateCustomAttributeDefinition({
          entity,
          definition,
          partnerId,
          customAttributeDefinitionId: customAttributeDefinition.customAttributeDefinitionId,
        });
      } else {
        customAttribute = await createCustomAttributeDefinition({
          entity,
          definition,
          partnerId,
        });
      }

      return customAttribute;
    },
    [createCustomAttributeDefinition, updateCustomAttributeDefinition],
  );

  return {
    saveCustomAttributeDefinition,
    createCustomAttributeDefinition,
    deleteCustomAttribute,
    updateCustomAttributeDefinition,
  };
};

const EMPTY_DEFINITIONS: CustomAttributeDefinition[] = [];

export const useCustomAttributeDefinitions = (
  params: {
    partnerId?: number | null;
    entity?: string | null;
  },
  options?: QueryHookOptions<GetCustomAttributeDefinitions> & {
    desc?: boolean;
    filters?: ConditionInput[];
  },
) => {
  const { partnerId, entity } = params ?? {};

  const { includePlatformLevelData: includePlatformPartner } = usePlatformCatalogAccess(
    'CustomAttributeDefinition',
    partnerId,
  );

  const filters = getFilters({ partnerId, entity, includePlatformPartner });

  const { desc = true, filters: extraFilters = [], skip = false } = options ?? {};

  const { data, ...rest } = useQuery<GetCustomAttributeDefinitions, QueryVariables>(
    GET_CUSTOM_ATTRIBUTE_DEFINITION_QUERY,
    {
      ...options,
      context: {
        includePlatformLevelData: includePlatformPartner,
      },
      skip: skip || isNullish(partnerId),
      variables: {
        params: {
          filters: [...filters, ...extraFilters],
          sorting: { attribute: 'updatedAt', desc },
        },
      },
    },
  );

  const customAttributeDefinitions =
    data?.getCustomAttributeDefinition.records ?? EMPTY_DEFINITIONS;

  const operations = useCustomAttributeDefinition({
    desc,
    filters: [...filters, ...extraFilters],
  });

  return {
    customAttributeDefinitions,
    ...rest,
    ...operations,
  };
};

export function getFilters({
  partnerId,
  entity,
  includePlatformPartner = true,
}: {
  partnerId?: number | null;
  entity?: string | null;
  includePlatformPartner?: boolean;
}) {
  const filters: ConditionInput[] = [];
  const partnerIds: number[] = [];

  if (includePlatformPartner) {
    partnerIds.push(PLATFORM_PARTNER_ID);
  }

  if (!isNullish(partnerId)) {
    partnerIds.push(partnerId);
  }

  if (partnerIds.length) {
    filters.push(
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: partnerIds.join(','),
      },
    );
  }

  if (entity) {
    filters.push({
      attribute: 'entity',
      operator: '=',
      value: entity,
    });
  }

  return filters;
}
